/**
 * Skeleton loading components.
 *
 * Building blocks: SkeletonLine, SkeletonCard
 * Page composites: ProfileListSkeleton, ProfilePageSkeleton
 */

// ── Building blocks ──

function SkeletonLine({ width = '100%', height }: { width?: string; height?: string }) {
  return <div className="skeleton" style={{ width, height }} />;
}

function SkeletonCard({ hasHeader = true, children }: { hasHeader?: boolean; children?: React.ReactNode }) {
  return (
    <div className="skeleton-card">
      {hasHeader && (
        <div className="skeleton-card-header">
          <SkeletonLine width="40%" height="0.85rem" />
        </div>
      )}
      <div className="skeleton-card-body">{children}</div>
    </div>
  );
}

// ── Page composites ──

/** Profile list page — grid of profile cards */
export function ProfileListSkeleton() {
  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <SkeletonLine width="55%" height="1.5rem" />
        <div style={{ marginTop: '0.5rem' }}>
          <SkeletonLine width="75%" height="0.75rem" />
        </div>
      </div>
      <div className="card-grid">
        {[0, 1].map(i => (
          <SkeletonCard key={i}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[0, 1, 2, 3].map(j => (
                <div key={j}>
                  <SkeletonLine width="60%" height="0.55rem" />
                  <div style={{ marginTop: '0.35rem' }}>
                    <SkeletonLine width="80%" height="1rem" />
                  </div>
                </div>
              ))}
            </div>
          </SkeletonCard>
        ))}
      </div>
    </div>
  );
}

/** Profile page / Dashboard — header + financial summary + property list */
export function ProfilePageSkeleton() {
  return (
    <div className="page-container">
      {/* Profile name */}
      <SkeletonLine width="45%" height="1.75rem" />
      <div style={{ marginTop: '0.35rem', marginBottom: '1.5rem' }}>
        <SkeletonLine width="25%" height="0.65rem" />
      </div>

      {/* Financial Summary card */}
      <SkeletonCard>
        <div className="financial-summary-grid">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} style={{ textAlign: 'center' }}>
              <SkeletonLine width="70%" height="1.3rem" />
              <div style={{ marginTop: '0.35rem' }}>
                <SkeletonLine width="80%" height="0.55rem" />
              </div>
            </div>
          ))}
        </div>
      </SkeletonCard>

      {/* Section heading placeholder */}
      <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
        <SkeletonLine width="160px" height="0.85rem" />
      </div>

      {/* Saved Properties heading */}
      <div style={{ marginBottom: '0.75rem' }}>
        <SkeletonLine width="140px" height="1.1rem" />
      </div>

      {/* Property rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {[0, 1, 2].map(i => (
          <div key={i} className="skeleton-card">
            <div className="skeleton-card-body" style={{ padding: '0.75rem 1rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                {/* Indicator dot */}
                <div className="skeleton" style={{ width: 10, height: 10, borderRadius: '50%', marginTop: '0.3rem', flexShrink: 0 }} />
                {/* Address lines */}
                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                  <SkeletonLine width="70%" height="0.85rem" />
                  <div style={{ marginTop: '0.35rem' }}>
                    <SkeletonLine width="45%" height="0.65rem" />
                  </div>
                </div>
                {/* Price */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <SkeletonLine width="90px" height="0.9rem" />
                  <div style={{ marginTop: '0.3rem' }}>
                    <SkeletonLine width="75px" height="0.6rem" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
